import type {
  GrowerGeoResponse,
  LocationResponse,
  SiteGeoResponse,
} from "api/model";
import {
  hasBoundaryGeoJson,
  isPointInPolygon,
} from "context/PlotMap/boundaryUtils";
import { toRadians } from "context/PlotMap/gridMeshGeometry";
import type { OrthoImageCoordinates } from "context/OrthoWorkflow/orthoUpload";

import { getBoundariesBounds } from "./mapCameraUtils";

const EARTH_RADIUS_MILES = 3958.8;
export const LOCATION_RADIUS_MILES = 10;

const haversineDistanceMiles = (
  a: { latitude: number; longitude: number },
  b: { latitude: number; longitude: number },
): number => {
  const dLat = toRadians(b.latitude - a.latitude);
  const dLng = toRadians(b.longitude - a.longitude);
  const lat1 = toRadians(a.latitude);
  const lat2 = toRadians(b.latitude);

  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;

  return 2 * EARTH_RADIUS_MILES * Math.asin(Math.sqrt(h));
};

/**
 * Returns null when there isn't enough data to judge (no flight points or no
 * location coordinates), otherwise whether every flight point falls within
 * LOCATION_RADIUS_MILES of the location's coordinates.
 */
export const isFlightWithinLocationRadius = (
  flightPoints: OrthoImageCoordinates[],
  location: LocationResponse,
): boolean | null => {
  if (
    flightPoints.length === 0 ||
    location.latitude == null ||
    location.longitude == null
  ) {
    return null;
  }

  const locationPoint = {
    latitude: location.latitude,
    longitude: location.longitude,
  };

  return flightPoints.every(
    (point) =>
      haversineDistanceMiles(point, locationPoint) <= LOCATION_RADIUS_MILES,
  );
};

/** Camera target for the review-flight map: all grower boundaries, immediately. */
export const computePreviewBounds = (growers: GrowerGeoResponse[]) =>
  getBoundariesBounds(growers.map((grower) => grower.boundary));

export const countFramesPerSite = (
  coords: OrthoImageCoordinates[],
  sites: SiteGeoResponse[],
): Record<number, number> => {
  const counts: Record<number, number> = {};
  for (const site of sites) {
    if (site.boundary == null || !hasBoundaryGeoJson(site.boundary)) {
      counts[site.id] = 0;
      continue;
    }
    counts[site.id] = coords.filter((coord) =>
      site.boundary!.coordinates.some((polygon) => {
        const outerRing = polygon[0] as [number, number][] | undefined;
        return outerRing != null && isPointInPolygon(coord, outerRing);
      }),
    ).length;
  }
  return counts;
};

/** Site with the most flight frames, or null if none have any. */
export const getBestSiteId = (
  framesPerSite: Readonly<Record<number, number>>,
): number | null => {
  let bestId: number | null = null;
  let bestCount = 0;
  for (const [siteId, count] of Object.entries(framesPerSite)) {
    if (count > bestCount) {
      bestId = Number(siteId);
      bestCount = count;
    }
  }
  return bestId;
};
